import BaseModel from "./BaseModel";
import BaseObject from "./BaseObject";
import ObjectFactory from "./ObjectFactory";
import CableTypeTreeViewModel from "../modelTreeViews/CableTypeTreeviewModel";
import type ConnectableObject from "./ConnectableObject";
import { itx, T } from "../translationItx/itx";
import Consumer from "./Consumer";
import { sortArrayByProperty } from "./utils";
import { DragMode, DropResult } from "./TreeviewModel";
import { isDevelopmentEnvironment } from "../estada-config";
import { ChannelType, channelTypeInfo, electricalChannelType, isSwitchedPlugChannelType, isCablelessPlugChannelType } from "./ChannelType";

ObjectFactory.registerClass("CableType", (data?: any, parent?: BaseModel) => {
  return new CableType(data, parent);
});

export type Wire = {
  htmlColor: string;
  longName: string;
  shortName: string;
};

export function getWireDefinitions(): Array<Wire> {
  return [
    {
      // 0
      htmlColor: "#b7d12a", // gelb/grün,
      longName: itx("cableType.long.yellow/green"),
      shortName: itx("cableType.short.yellow/green"),
    }, // erde
    {
      // 1
      htmlColor: "#0066b3", // blau,
      longName: itx("cableType.long.blue"),
      shortName: itx("cableType.short.blue"),
    }, // nulleiter

    {
      // 2
      htmlColor: "#a0522d", // braun
      longName: itx("cableType.long.brown"),
      shortName: itx("cableType.short.brown"),
    },

    {
      // 3
      htmlColor: "#000000", // schwarz
      longName: itx("cableType.long.black"),
      shortName: itx("cableType.short.black"),
    },

    {
      // 4
      htmlColor: "#bdbdbd", // grau
      longName: itx("cableType.long.grey"),
      shortName: itx("cableType.short.grey"),
    },
    {
      // 5
      htmlColor: "#a020f0", // violett
      longName: itx("cableType.long.violet"),
      shortName: itx("cableType.short.violet"),
    },
    {
      // 6
      htmlColor: "#ffffff", // weiß
      longName: itx("cableType.long.white"),
      shortName: itx("cableType.short.white"),
    },

    {
      // 7
      htmlColor: "#ff9900", // orange
      longName: itx("cableType.long.orange"),
      shortName: itx("cableType.short.orange"),
    },

    {
      // 8
      htmlColor: "#ff0000", // rot
      longName: itx("cableType.long.red"),
      shortName: itx("cableType.short.red"),
    },
  ];
}

export class CableType extends BaseModel {
  public get className(): string {
    return "CableType"; // no itx
  }

  private getWireNames(
    code: number,
    type: "short" | "long", // no itx
  ): Array<string> {
    let res = new Array<string>();
    getWireDefinitions().forEach((definition) => {
      if (
        type == "long" // no itx
      ) {
        res.push(definition.longName);
      } else {
        res.push(definition.shortName);
      }
    });
    return res;
  }

  get wireDefinitions(): Array<Wire> {
    return getWireDefinitions();
  }

  _numberOfWires = -1;

  // -----------------------------------------------------------------------------
  // isDefault
  // -----------------------------------------------------------------------------
  _isDefault: boolean = false;
  _isLan: boolean = false;
  get isLan(): boolean {
    return this._isLan;
  }
  set isLan(newValue: boolean) {
    this._isLan = newValue;
  }
  get isDefault(): boolean {
    return this._isDefault;
  }
  set isDefault(newValue: boolean) {
    this.beforeChange("isDefault");
    if (newValue) {
      state.cableTypes.forEach((cableType) => {
        if (cableType.isDefault) {
          cableType.isDefault = false;
        }
      });
      this._isDefault = newValue;
    } else if (this._isDefault) {
      this._isDefault = newValue;
      // // no Cable Type should be default
      // state.cableTypes.asArray()[0]._isDefault = true;
    }

    this.changed("isDefault");
  }

  // -----------------------------------------------------------------------------

  get numberOfWires(): number {
    if (this._numberOfWires < 0) {
      const n = this.name.match(/(\d+)x/)?.[1];
      if (n) {
        const nn = parseInt(n);
        return nn;
      }
      let ss = this.name.split("x");
      ss = this.tag.split(".");
      if (ss.length > 1) {
        const n = parseInt(ss[0]);
        if (n >= 2) {
          return n;
        }
      }
    } else {
      return this._numberOfWires;
    }

    return 3; // default
  }

  get numberOfWiresWithoutPEandN(): number {
    const numberOfWires = this.numberOfWires;
    if (this.hasGround) {
      return Math.max(0, numberOfWires - 2); // remove PE and N
    } else {
      return Math.max(0, numberOfWires - 1); // remove N
    }
  }

  set numberOfWires(newValue: number) {
    this.beforeChange("numberOfWires");
    this._numberOfWires = newValue;

    this.changed("numberOfWires");
  }

  get consumers(): Array<Consumer> {
    let consumers = state.allConsumers().filter((consumer) => consumer.cableType == this);
    consumers = sortArrayByProperty(consumers, "fullTag");
    return consumers;
  }

  // -----------------------------------------------------------------------------
  // diameter
  // -----------------------------------------------------------------------------
  _diameter: number = -1;

  set diameter(newValue: number) {
    this.beforeChange("diameter");
    this._diameter = newValue;

    this.changed("diameter");
  }

  get diameter(): number {
    if (this._diameter >= 0) {
      return this._diameter;
    }
    // get diameter from name
    for (let n in this.name.split(" ")) {
      const ss = n.split("x");
      if (ss.length > 1) {
        const n = parseInt(ss[1]);
        if (n > 0) {
          return n;
        }
      }
    }

    return 1.5;
  }

  // -----------------------------------------------------------------------------
  // hasGround
  // -----------------------------------------------------------------------------
  _hasGround = true;
  get hasGround(): boolean {
    return this._hasGround;
  }

  set hasGround(newValue: boolean) {
    if (this._hasGround === newValue) return;
    this.beforeChange("hasGround");
    this._hasGround = newValue;
    this.changed("hasGround");
  }

  // -----------------------------------------------------------------------------

  _wireNames?: Array<string>;

  set wireNames(newValue: Array<string>) {
    this._wireNames = newValue;
  }

  get htmlColors(): Array<string> {
    let res = new Array<string>();

    getWireDefinitions().forEach((definition) => {
      res.push(definition.htmlColor);
    });

    return res;
  }

  get wireNames(): Array<string> {
    if (this._wireNames) {
      return this._wireNames;
    }
    return this.getWireNames(this.numberOfWires, "long"); // no itx
  }

  // -----------------------------------------------------------------------------
  // wireShortNames
  // -----------------------------------------------------------------------------
  _wireShortNames?: Array<string>;

  set wireShortNames(newValue: Array<string>) {
    this._wireShortNames = newValue;
  }

  get wireShortNames(): Array<string> {
    if (this._wireShortNames) {
      return this._wireShortNames;
    }
    return this.getWireNames(this.numberOfWires, "short"); // no itx
  }

  // -----------------------------------------------------------------------------

  constructor(data?: any, parent?: BaseObject) {
    // constructor(data: { tag: string; name: string; numberOfWires: number }, parent?: BaseObject) {
    super(data, parent);
    if (data) {
      this.numberOfWires = data.numberOfWires;
      this.isDefault = data.isDefault;
    }
    this.treeViewModel = new CableTypeTreeViewModel(this);
  }

  get consumerUsages(): Array<ConnectableObject> {
    const res = new Array<ConnectableObject>();
    state.allConsumers().forEach((cons) => {
      if (cons.cableType == this) {
        res.push(cons);
      }
    });
    return res;
  }

  getPersistentProperties(): string[] {
    return [
      "_numberOfWires",
      "_diameter",
      "hasGround",
      "wiresNames",
      "isDefault",
      "_isLan",
      ...super.getPersistentProperties(),
    ]; // no itx
  }

  getIsDefaultForConsumer(consumer: Consumer): boolean {
    // Steckdosen: NYM-J 5x2,5 mm² (auch schaltbare)-
    //   Licht: NYM-J 5x1,5 mm² (Alles Typen Licht)-
    //   Verbraucher 400V: NYM-J 5x2,5 mm² -
    //   Verbraucher 230V: NYM-J 5x1,5 mm² -
    //   Rollläden/Jalousien: NYM-J 5x1,5 mm²-
    //   Kontakt: YCYM Y(St)Y2x2x0,8 (lila)
    //   Heizung: -
    if (isCablelessPlugChannelType(consumer.channelType)) return false;
    if (consumer.channelType === ChannelType.plug_230V || isSwitchedPlugChannelType(consumer.channelType) || consumer.channelType == ChannelType.suply400V) {
      return this.tag == "C5.25";
    } else if (
      channelTypeInfo(consumer.channelType).isLight ||
      consumer.channelType == ChannelType.suply230V ||
      channelTypeInfo(consumer.channelType).isRollo
    ) {
      return this.tag == "C5.15";
    } else if (consumer.channelType == ChannelType.Lock) {
      return this.tag == "Cy4.08";
    } else if (consumer.channelType == ChannelType.Heating) {
      return this.tag == "C0";
    } else if (electricalChannelType(consumer.channelType) == ChannelType.Lan2) {
      // dedicated dual-LAN cable, since a Lan2 consumer occupies 2 actor-channel ports
      return this.tag == "CLan2";
    } else if (channelTypeInfo(consumer.channelType).isLan) {
      // first lancabletype
      return this.tag == "CLan";
    }

    return this.tag == "C0";
  }
  canBeDeletedInteractive(): boolean {
    return false;
  }

  getDeleteConfirmText(): string | undefined {
    if (!this.canBeDeletedInteractive()) {
      const T1 = this.name;

      return state.translate("menuBar.doYouWantToT1TheClassNameT2", "T1 hat noch T2", {
        T1: itx("homePage.delete"),
        T2: `${T1}`,
        className: state.translate("components.CableType", "KabelType"),
      });
    } else {
      return super.getDeleteConfirmText();
    }
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    if (obj.className == "Consumer") {
      const consumer = obj as Consumer;

      // LAN cable type is exclusively for LAN consumers and vice versa
      if (this.isLan && !channelTypeInfo(consumer.channelType).isLan) {
        return new DropResult(false);
      }
      if (!this.isLan && channelTypeInfo(consumer.channelType).isLan) {
        return new DropResult(false);
      }

      consumer.cableType = this;
      consumer.raiseOnChange("cableType");
      this.raiseOnChange("childCollection");

      return new DropResult(true);
    }

    return new DropResult(false);
  }
}

export function initCableTypes() {
  // // check for empty names and delete this cable type
  // state.cableTypes.forEach((cableType) => {
  //   if (!cableType.name || cableType.name.trim() == "") {
  //     state.cableTypes.remove(cableType);
  //   }
  // });
  if (state.cableTypes.size == 0) {
    // state.cableTypes.addWithoutUpdate(
    //   new CableType(
    //     {
    //       tag: "none",
    //       name: "-/-",
    //       numberOfWires: 0,
    //     },
    //     state.cableTypes,
    //   ),
    // );

    /////////////////////////////////////////////////////////
    const cabletypes = [
      { tag: "C3.15", name: "NYM-J 3x1.5 mm²", numberOfWires: 3, diameter: 1.5 }, // no itx
      { tag: "C3.25", name: "NYM-J 3x2.5 mm²", numberOfWires: 3, diameter: 2.5 }, // no itx
      { tag: "C5.25", name: "NYM-J 5x2.5 mm²", numberOfWires: 5, diameter: 2.5 }, // no itx
      { tag: "C5.15", name: "NYM-J 5x1.5 mm²", numberOfWires: 5, diameter: 1.5 }, // no itx
      { tag: "C0", name: itx("", "-(kein Kabeltype definiert)"), numberOfWires: 0, diameter: 0 }, // no itx
      { tag: "CLan", name: "LAN-Kabel (Simplex)", numberOfWires: 8, diameter: 0.5, isLan: true }, // no itx
      { tag: "CLan2", name: "LAN-Kabel (Duplex)", numberOfWires: 8, diameter: 0.5, isLan: true }, // no itx
    ];
    if (isDevelopmentEnvironment()) {
      cabletypes.push({ tag: "C4.08", name: "YCYM Y(St)2x2x0.8 mm²", numberOfWires: 4, diameter: 0.8 });
      cabletypes.push({ tag: "Cy4.08", name: "YCYM Y(St)Y2x2x0.8 mm²(lila)", numberOfWires: 4, diameter: 0.8 });
    }
    const defaultCableTypeTag = "C5.15";
    // add default cable types
    //
    // add cable types
    cabletypes.forEach((ct) => {
      const { tag, name, numberOfWires, diameter, isLan } = ct;
      const newCableType = new CableType(
        {
          tag: tag, // no itx
          name: name, // no itx
          numberOfWires: numberOfWires,
          diameter: diameter,
        },
        state.cableTypes,
      );
      newCableType.tag = tag;
      newCableType.name = name;
      newCableType.numberOfWires = numberOfWires;
      newCableType.diameter = diameter;
      newCableType.isLan = isLan || false;
      if (tag == defaultCableTypeTag) {
        newCableType.isDefault = true;
      }
      state.cableTypes.addWithoutUpdate(newCableType);
    });
  } else {
    //
    // delete duplicates
    //
    const m = new Map<string, CableType>();
    state.cableTypes.forEach((cableType) => {
      m.set(cableType.tag, cableType);
    });
    state.cableTypes.items.clear();
    m.forEach((cableType) => {
      state.cableTypes.addWithoutUpdate(cableType);
    });
    // ensure CLan exists for projects created before it was introduced
    if (!m.has("CLan")) {
      const cLan = new CableType(
        { tag: "CLan", name: "LAN-Kabel (Simplex)", numberOfWires: 8, diameter: 0.5 },
        state.cableTypes,
      ); // no itx
      cLan.isLan = true;
      state.cableTypes.addWithoutUpdate(cLan);
    }
    // ensure CLan2 exists for projects created before it was introduced
    if (!m.has("CLan2")) {
      const cLan2 = new CableType(
        { tag: "CLan2", name: "LAN-Kabel (Duplex)", numberOfWires: 8, diameter: 0.5 }, // no itx
        state.cableTypes,
      );
      cLan2.isLan = true;
      state.cableTypes.addWithoutUpdate(cLan2);
    }
  }

  /////////////////////////////////////////////////////////
}
